import { AddToCartProductInput } from '@Generated/graphql'

type Entry = {
  isSelected: boolean
  items: readonly AddToCartProductInput[]
}

const EMPTY: readonly AddToCartProductInput[] = Object.freeze([])

const registry = new Map<string, Entry>()
const selectionListeners = new Map<string, Set<() => void>>()
const snapshotListeners = new Set<() => void>()
let snapshot: readonly AddToCartProductInput[] = EMPTY

function notifySelection(id: string) {
  selectionListeners.get(id)?.forEach(l => l())
}

function recomputeSnapshot(): boolean {
  const next: AddToCartProductInput[] = []
  registry.forEach((entry) => {
    if (entry.isSelected) {
      next.push(...entry.items)
    }
  })

  if (next.length === snapshot.length) {
    let same = true
    for (let i = 0; i < next.length; i += 1) {
      if (next[i] !== snapshot[i]) {
        same = false
        break
      }
    }
    if (same) {
      return false
    }
  }

  snapshot = next

  return true
}

function notifySnapshotIfChanged() {
  if (recomputeSnapshot()) {
    snapshotListeners.forEach(l => l())
  }
}

export function getSelectedAddons() {
  const out: AddToCartProductInput[] = []

  registry.forEach((entry) => {
    if (!entry.isSelected) {
      return
    }

    out.push(...entry.items)
  })

  return Object.freeze(out)
}

export const addonStore = {
  register(id: string, items: readonly AddToCartProductInput[]) {
    const entry = registry.get(id)
    if (entry) {
      entry.items = items
    } else {
      registry.set(id, { items, isSelected: false })
    }

    notifySnapshotIfChanged()
  },

  unregister(id: string) {
    if (!registry.delete(id)) {
      return
    }

    notifySnapshotIfChanged()
  },

  isSelected(id: string) {
    return registry.get(id)?.isSelected ?? false
  },

  setIsSelected(id: string, value: boolean) {
    const entry = registry.get(id)
    if (!entry || entry.isSelected === value) {
      return
    }

    entry.isSelected = value

    notifySelection(id)
    notifySnapshotIfChanged()
  },

  reset() {
    const flippedIds: string[] = []

    registry.forEach((entry, id) => {
      if (entry.isSelected) {
        // eslint-disable-next-line no-param-reassign
        entry.isSelected = false
        flippedIds.push(id)
      }
    })

    flippedIds.forEach(notifySelection)
    notifySnapshotIfChanged()
  },

  subscribeSelection(id: string, listener: () => void) {
    let set = selectionListeners.get(id)
    if (!set) {
      set = new Set()
      selectionListeners.set(id, set)
    }

    set.add(listener)

    return () => {
      set!.delete(listener)

      if (set!.size === 0) {
        selectionListeners.delete(id)
      }
    }
  },

  subscribeSnapshot(listener: () => void) {
    snapshotListeners.add(listener)

    return () => {
      snapshotListeners.delete(listener)
    }
  },

  getSnapshot() {
    return snapshot
  },

  getServerSnapshot() {
    return EMPTY
  },
}
